import React, { useEffect, useContext, useMemo } from "react"
import PropTypes from "prop-types"
import { useNavigate } from "react-router-dom"
import { Box, Button, Card, Modal, Stack, Typography } from "@mui/material"
import CreditCardRounded from "@mui/icons-material/CreditCardRounded"
import CalendarMonthRounded from "@mui/icons-material/CalendarMonthRounded"
import ChatRounded from "@mui/icons-material/ChatRounded"
import ArrowForwardRounded from "@mui/icons-material/ArrowForwardRounded"
import i18n from "i18n"
import AuthContext from "context/auth/authContext"
import SubscriptionContext from "context/subscription/subscriptionContext"
import { isOnAuthPage } from "utils/functions"

const SmallIconBox = ({ children, bg, color }) => (
  <Box
    sx={{
      width: 54,
      height: 54,
      borderRadius: "4px",
      display: "grid",
      placeItems: "center",
      bgcolor: bg,
      color,
      flexShrink: 0,
    }}
  >
    {children}
  </Box>
)

SmallIconBox.propTypes = {
  children: PropTypes.node.isRequired,
  bg: PropTypes.string.isRequired,
  color: PropTypes.string.isRequired,
}

const SecondaryCard = ({
  icon,
  title,
  description,
  cta,
  onClick,
  iconBg,
  iconColor,
}) => (
  <Card
    elevation={0}
    sx={{
      p: { xs: 2.25, md: 2.5 },
      borderRadius: "4px",
      bgcolor: "#ffffff",
      height: "100%",
    }}
  >
    <Stack spacing={2} sx={{ height: "100%" }}>
      <Stack direction="row" spacing={1.5} alignItems="center">
        <SmallIconBox bg={iconBg} color={iconColor}>
          {icon}
        </SmallIconBox>
        <Typography
          sx={{
            fontSize: { xs: 20, md: 21 },
            fontWeight: 700,
            color: "#111827",
          }}
        >
          {title}
        </Typography>
      </Stack>

      {description ? (
        <Typography
          sx={{
            fontSize: { xs: 15, md: 15.5 },
            color: "#475569",
            lineHeight: 1.55,
          }}
        >
          {description}
        </Typography>
      ) : null}

      <Box sx={{ mt: "auto" }}>
        <Button
          fullWidth
          disableElevation
          onClick={onClick}
          sx={{
            minHeight: 40,
            bgcolor: "#ffffff",
            color: "#1f2937",
            border: "1px solid #dbe3ef",
            textTransform: "none",
            fontWeight: 700,
            "&:hover": {
              bgcolor: "#f8fafc",
            },
          }}
        >
          {cta}
        </Button>
      </Box>
    </Stack>
  </Card>
)

SecondaryCard.propTypes = {
  icon: PropTypes.node.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  cta: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
  iconBg: PropTypes.string.isRequired,
  iconColor: PropTypes.string.isRequired,
}

const ModalExpireFreeTrial = () => {
  const navigate = useNavigate()
  const { user, getUser, logOut } = useContext(AuthContext)
  const { modalExpireFreeTrial } = useContext(SubscriptionContext)

  const isCancelled = useMemo(
    () => user?.["custom:subscription_status"] === "cancelled",
    [user],
  )

  useEffect(() => {
    if (!user && !isOnAuthPage()) {
      getUser()
    }
  }, [user, getUser])

  const handleSubscription = () => {
    navigate("/subscription")
  }

  const handleConsultation = () => {
    window.open(
      "https://meetings-eu1.hubspot.com/meetings/tizianopierini/trial-demo-",
      "_blank",
      "noopener,noreferrer",
    )
  }

  const handleChat = () => {
    if (
      typeof window === "undefined" ||
      typeof window.Intercom !== "function"
    ) {
      return
    }

    window.Intercom("showNewMessage")
  }

  const handleLogout = () => {
    logOut()
    setTimeout(() => {
      navigate("/")
      window.location.reload()
    }, 1000)
  }

  return (
    <Modal
      open={modalExpireFreeTrial.open}
      onClose={() => {}}
      sx={{ zIndex: 1100 }}
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "92%", sm: "88%", md: 900 },
          maxWidth: "92vw",
          maxHeight: "90vh",
          overflowY: "auto",
          bgcolor: "#f6f7fb",
          borderRadius: "4px",
          p: { xs: 2.5, md: 3.5 },
        }}
      >
        <Box sx={{ maxWidth: 820, mx: "auto" }}>
          <Stack spacing={1.1} alignItems="center">
            <Box
              sx={{
                px: 1.5,
                py: 0.75,
                borderRadius: "4px",
                bgcolor: "#ffecb3",
                color: "#414141",
                fontWeight: 800,
                fontSize: 13,
              }}
            >
              {isCancelled
                ? i18n.t("modalExpireFreeTrial.badgeCancelled")
                : i18n.t("modalExpireFreeTrial.badgeTrial")}
            </Box>

            <Typography
              align="center"
              sx={{
                fontSize: { xs: 28, md: 36 },
                fontWeight: 800,
              }}
            >
              {isCancelled
                ? i18n.t("modalExpireFreeTrial.titleCancelled")
                : i18n.t("modalExpireFreeTrial.titleTrial")}
            </Typography>

            <Typography
              align="center"
              sx={{
                fontSize: { xs: 16, md: 18 },
                color: "#414141",
                maxWidth: 620,
              }}
            >
              {i18n.t("modalExpireFreeTrial.subtitle")}
            </Typography>
          </Stack>

          <Card
            elevation={0}
            sx={{
              mt: { xs: 2.75, md: 3.25 },
              p: { xs: 2.25, md: 2.75 },
              borderRadius: "4px",
              bgcolor: "#fffdf4",
            }}
          >
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={{ xs: 2, md: 2 }}
              alignItems={{ xs: "flex-start", md: "center" }}
              justifyContent="space-between"
            >
              <Stack
                direction="row"
                spacing={1.5}
                alignItems="flex-start"
                sx={{ maxWidth: 520 }}
              >
                <SmallIconBox bg="#ffd52b" color="#000000">
                  <CreditCardRounded />
                </SmallIconBox>

                <Box>
                  <Typography
                    sx={{
                      fontSize: { xs: 22, md: 27 },
                      fontWeight: 800,
                      lineHeight: 1.2,
                    }}
                  >
                    {i18n.t("modalExpireFreeTrial.reactivateTitle")}
                  </Typography>

                  <Typography
                    sx={{
                      mt: 0.6,
                      fontSize: { xs: 15.5, md: 16 },
                      color: "#414141",
                      lineHeight: 1.55,
                    }}
                  >
                    {i18n.t("modalExpireFreeTrial.reactivateDescription")}
                  </Typography>
                </Box>
              </Stack>

              <Button
                disableElevation
                onClick={handleSubscription}
                endIcon={<ArrowForwardRounded />}
                sx={{
                  minWidth: { xs: "100%", md: 210 },
                  minHeight: 52,
                  bgcolor: "#000000",
                  color: "#ffffff",
                  px: 2.5,
                  textTransform: "none",
                  fontWeight: 700,
                  flexShrink: 0,
                  "&:hover": {
                    bgcolor: "#111111",
                  },
                }}
              >
                {i18n.t("modalExpireFreeTrial.viewPlans")}
              </Button>
            </Stack>
          </Card>

          <Card
            elevation={0}
            sx={{
              mt: 2,
              p: { xs: 2, md: 2.25 },
              borderRadius: "4px",
              bgcolor: "#ffffff",
            }}
          >
            <Stack spacing={1} alignItems="center">
              <Typography
                align="center"
                sx={{
                  fontSize: { xs: 17, md: 18 },
                  fontWeight: 800,
                }}
              >
                {i18n.t("modalExpireFreeTrial.helpTitle")}
              </Typography>
              <Typography
                align="center"
                sx={{
                  fontSize: { xs: 14.5, md: 15 },
                  color: "#414141",
                  lineHeight: 1.55,
                  maxWidth: 560,
                }}
              >
                {i18n.t("modalExpireFreeTrial.helpDescription")}
              </Typography>
            </Stack>
          </Card>

          <Box
            sx={{
              mt: 2,
              display: "grid",
              gap: 2,
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, minmax(0, 1fr))",
              },
              alignItems: "stretch",
            }}
          >
            <SecondaryCard
              icon={<CalendarMonthRounded />}
              title={i18n.t("modalExpireFreeTrial.consultationTitle")}
              cta={i18n.t("modalExpireFreeTrial.consultationCta")}
              onClick={handleConsultation}
              iconBg="#eef2ff"
              iconColor="#4338ca"
            />

            <SecondaryCard
              icon={<ChatRounded />}
              title={i18n.t("modalExpireFreeTrial.chatTitle")}
              cta={i18n.t("modalExpireFreeTrial.chatCta")}
              onClick={handleChat}
              iconBg="#ecfdf3"
              iconColor="#16a34a"
            />
          </Box>

          <Button
            variant="text"
            onClick={handleLogout}
            sx={{
              mt: 2.25,
              display: "block",
              mx: "auto",
              textTransform: "none",
              borderRadius: "4px",
              minHeight: 40,
              color: "#64748b",
              fontWeight: 600,
            }}
          >
            {i18n.t("modalExpireFreeTrial.logout")}
          </Button>
        </Box>
      </Box>
    </Modal>
  )
}

export default ModalExpireFreeTrial
